/**
 * Pricing / signup email checks: basic format + common provider typos
 * (e.g. gmaiil.com, gmail.comn) that still look like valid emails to a loose regex.
 */

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const POPULAR_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'mail.com',
  'msn.com',
] as const;

/** Known mistypes → suggested domain */
const DOMAIN_TYPOS: Record<string, string> = {
  'gmaiil.com': 'gmail.com',
  'gmail.comn': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.cpm': 'gmail.com',
  'gmail.vom': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmail.om': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.comn': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlook.comn': 'outlook.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.comn': 'yahoo.com',
};

/** TLDs that are almost always typos of .com / .net / etc. */
const TYPO_TLDS = new Set([
  'comn',
  'con',
  'cpm',
  'vom',
  'xom',
  'comm',
  'coom',
  'ocm',
  'coml',
  'nett',
  'ner',
]);

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

export function validateSignupEmail(raw: string): { ok: true } | { ok: false; message: string } {
  const email = raw.trim().toLowerCase();
  if (!email) {
    return { ok: false, message: 'Email is required' };
  }
  if (!EMAIL_FORMAT.test(email) || email.includes('..')) {
    return { ok: false, message: 'Please enter a valid email address.' };
  }

  const at = email.lastIndexOf('@');
  const domain = email.slice(at + 1);
  if (!domain || domain.startsWith('.') || domain.endsWith('.')) {
    return { ok: false, message: 'Please enter a valid email address.' };
  }

  const tld = domain.split('.').pop() || '';
  if (TYPO_TLDS.has(tld)) {
    return { ok: false, message: 'Please enter a valid email address.' };
  }

  const typoSuggestion = DOMAIN_TYPOS[domain];
  if (typoSuggestion) {
    return { ok: false, message: `Please check your email — did you mean ${typoSuggestion}?` };
  }

  for (const popular of POPULAR_DOMAINS) {
    if (domain === popular) {
      return { ok: true };
    }
    // Catch near-misses like gmaiil.com / gmail.comn without listing every variant
    const dist = levenshtein(domain, popular);
    if (dist > 0 && dist <= 2) {
      return { ok: false, message: `Please check your email — did you mean ${popular}?` };
    }
  }

  return { ok: true };
}
